/*
  Branded video playback via Plyr (MIT, vendored as plyr.min.js / plyr.css).

  Every video on the site is a poster frame until someone presses play, so Plyr
  itself is fetched on that first click rather than on page load — visitors who
  never watch pay nothing for it. If it fails to arrive, the click falls back to
  a plain provider iframe (or native <video> controls) so the video still plays.

  Providers: youtube / vimeo (data-video-id is the provider's id), and html5 for
  Shopify-hosted video, whose <video> waits in a <template> inside the poster.

  The poster button is replaced by the player, so focus is parked on the stage
  and then handed to Plyr's play/pause control once it exists — keyboard users
  are never dropped back to <body>.
*/
(() => {
  'use strict';

  if (window.psjVideoPlayer) return;
  window.psjVideoPlayer = true;

  let plyrPromise = null;

  const loadPlyr = () => {
    if (plyrPromise) return plyrPromise;

    const paths = window.theme?.plyr;
    if (!paths) return Promise.reject(new Error('Plyr asset paths missing'));

    plyrPromise = new Promise((resolve, reject) => {
      const css = document.createElement('link');
      css.rel = 'stylesheet';
      css.href = paths.css;
      document.head.appendChild(css);

      const script = document.createElement('script');
      script.src = paths.js;
      script.onload = () => (window.Plyr ? resolve(window.Plyr) : reject(new Error('Plyr did not register')));
      script.onerror = () => reject(new Error('Plyr failed to load'));
      document.head.appendChild(script);
    });

    return plyrPromise;
  };

  const fallbackIframe = (provider, id) => {
    const src = provider === 'vimeo'
      ? `https://player.vimeo.com/video/${id}?autoplay=1`
      : `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0&playsinline=1`;
    const frame = document.createElement('iframe');
    frame.src = src;
    frame.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
    frame.allowFullscreen = true;
    frame.setAttribute('title', 'Video');
    return frame;
  };

  // Only take focus if the shopper has not moved on while the player loaded.
  const focusIfParked = (stage, target) => {
    const active = document.activeElement;
    if (target && (active === stage || active === document.body || !active)) {
      target.focus({ preventScroll: true });
    }
  };

  document.addEventListener('click', async (event) => {
    const trigger = event.target.closest('[data-video-play]');
    if (!trigger) return;

    event.preventDefault();

    const { videoProvider: provider = 'youtube', videoId: id } = trigger.dataset;
    const template = trigger.querySelector('template');
    if (!id && !template) return;

    const stage = document.createElement('div');
    stage.className = trigger.dataset.stageClass || 'video-stage';
    stage.tabIndex = -1;

    // Swap the poster out first so the click always feels immediate.
    const hadFocus = document.activeElement === trigger || trigger.contains(document.activeElement);
    trigger.replaceWith(stage);
    if (hadFocus) stage.focus({ preventScroll: true });

    let video = null;
    if (provider === 'html5' && template) {
      stage.appendChild(template.content.cloneNode(true));
      video = stage.querySelector('video');
    }

    try {
      const Plyr = await loadPlyr();

      let target = video;
      if (!target) {
        target = document.createElement('div');
        target.dataset.plyrProvider = provider;
        target.dataset.plyrEmbedId = id;
        stage.appendChild(target);
      }

      const player = new Plyr(target, {
        autoplay: true,
        // Suggested videos at the end are limited to the shop's own channel.
        youtube: { noCookie: true, rel: 0, modestbranding: 1, iv_load_policy: 3 },
        controls: ['play-large', 'play', 'progress', 'current-time', 'mute', 'volume', 'fullscreen'],
      });

      player.on('ready', () => {
        player.play();
        focusIfParked(stage, stage.querySelector('.plyr__controls [data-plyr="play"]'));
      });
    } catch {
      if (video) {
        video.controls = true;
        video.play().catch(() => {});
        focusIfParked(stage, video);
      } else {
        const frame = fallbackIframe(provider, id);
        stage.appendChild(frame);
        focusIfParked(stage, frame);
      }
    }
  });

  /*
    YouTube posters: maxresdefault (1280×720) is sharp at the 765px slot but
    not every upload has one. A missing size comes back either as an error or
    as YouTube's 120×90 grey placeholder, so both fall back to the next size.
  */
  const POSTER_FALLBACKS = ['maxresdefault', 'sddefault', 'hqdefault'];

  const checkPoster = (img) => {
    if (!img.matches?.('img[data-youtube-poster]') || !img.complete) return;
    const failed = img.naturalWidth === 0 || img.naturalWidth <= 120;
    if (!failed) return;
    const current = POSTER_FALLBACKS.find((size) => img.src.includes(`/${size}.jpg`));
    const next = POSTER_FALLBACKS[POSTER_FALLBACKS.indexOf(current) + 1];
    if (current && next) img.src = img.src.replace(`/${current}.jpg`, `/${next}.jpg`);
  };

  // load/error do not bubble, but they can be caught on the way down.
  document.addEventListener('load', (event) => checkPoster(event.target), true);
  document.addEventListener('error', (event) => checkPoster(event.target), true);
  document.querySelectorAll('img[data-youtube-poster]').forEach(checkPoster);
})();
